import { useMemo } from 'react';
import {
  EMPTY_CELL,
  EXPAND_COLUMN_ID,
  IMAGE_COLUMN_ID,
  SELECT_COLUMN_ID,
} from './product-identifier-v2.constants';
import type {
  PendingBarcodeAction,
  RangePlanIdentifierRow,
  RangePlanTableColumn,
  ResolvedColumn,
  RetailCardSummary,
  ViewLabels,
} from './types';

/**
 * BM-9586 — the Range Plan Product Identifier column set.
 *
 * Split into a pure builder plus a thin memo hook so the contract can be tested without rendering
 * anything. Three rules govern it, each replacing something the v1 table gets wrong.
 *
 * **Order and visibility come from the server.** `ResolvedColumn[]` arrives already filtered for
 * the product's axes, brand and Retail Identifier scope, and already sorted. This file maps; it
 * does not sort or filter — with one exception, `lengthEnabled`, a per-user setting the server
 * does not know about, handled once here instead of the v1 table's three scattered guards.
 *
 * **Cells resolve by `identifierId`, never by column name.** The v1 table reads
 * `ele.initialData[col.name]`, so renaming a `ProductIdentifierMaster` silently blanks its column.
 *
 * **Retail columns need no special case.** A Retail Identifier column is just a column whose value
 * lives in the same `cells` map under `retail-type:<barcodeType>` — one column per barcode type, not
 * per card (BM-9211) — and each GTIN-14 group column under `factor:<id>` / `gtin14:<id>`. The server
 * folds them in, so there is one lookup rule for the whole table.
 *
 * The column list is the page's HEADER order only. Labels differ per product, so cell text is
 * resolved against the row's own product via `rowCellText` — a column that closed over one
 * product's labels would render every other product's ids as blanks.
 */

/** Row fields a system column can bind to, mapped to the label set that names them. */
const LABEL_SET_BY_FIELD: Readonly<Record<string, keyof ViewLabels>> = {
  colourwayId: 'colourwayId',
  sizeId: 'sizeId',
  lengthId: 'lengthId',
  sizeRangeId: 'sizeRangeId',
  additionalVariableId: 'additionalVariableId',
};

/**
 * The text one server column shows for one row.
 *
 * System columns hold an id and render its label; everything else — custom and retail alike —
 * reads `cells` by identifier id. An id with no label renders empty rather than the raw ObjectId,
 * which would look like corrupted data.
 *
 * @param row - The combination row.
 * @param column - The server column definition.
 * @param labels - The row's own product's id-to-label maps.
 * @returns Display text, or an empty string when there is nothing to show.
 */
export function cellText(
  row: RangePlanIdentifierRow,
  column: ResolvedColumn,
  labels: ViewLabels,
): string {
  if (column.field === 'productNumber') {
    return row.productNumber ?? '';
  }

  const labelSet = column.field ? LABEL_SET_BY_FIELD[column.field] : undefined;
  if (labelSet) {
    const id = row[column.field as keyof RangePlanIdentifierRow] as string;
    return id ? labels?.[labelSet]?.[id] ?? '' : '';
  }

  return row.cells?.[column.identifierId] ?? '';
}

/** As `cellText`, but falling back to the placeholder the v1 table and the sheet both use. */
export function rowCellText(
  row: RangePlanIdentifierRow,
  column: ResolvedColumn,
  labels: ViewLabels,
): string {
  return cellText(row, column, labels) || EMPTY_CELL;
}

/**
 * Whether a column survives this user's Length setting.
 *
 * The only filtering this module does. Everything else was already decided server-side.
 */
function isVisible(column: ResolvedColumn, lengthEnabled: boolean): boolean {
  return lengthEnabled || column.refCode !== 'length';
}

/**
 * Canonical column order, mirroring `SYSTEM_COLUMN_ORDER` in the product-identifier service.
 *
 * The server sorts each product's own columns, but the page's header row is the UNION across
 * products — and a union built in first-appearance order gets this wrong. A column only some
 * products have, like Variable 2, lands wherever its first owner happens to sit in the list: on a
 * page where the first product with a Variable 2 comes eighth, the column appears after every
 * custom column instead of before the Retail Identifier ones.
 */
const CANONICAL_REF_CODE_ORDER: ReadonlyArray<string> = [
  'productNumber',
  'colourway',
  '__sizeRange',
  'size',
  'length',
  '__additionalVariable',
  '__retailIdentifier',
];

/** Position of a column in the canonical order; custom columns sort after all of them. */
function canonicalIndex(column: ResolvedColumn): number {
  const refCode = isRetailGroup(column) ? '__retailIdentifier' : column.refCode;
  const index = refCode ? CANONICAL_REF_CODE_ORDER.indexOf(refCode) : -1;
  return index >= 0 ? index : CANONICAL_REF_CODE_ORDER.length;
}

/**
 * Builds the page's header row.
 *
 * Columns are the UNION across the page, in first-appearance order: two products legitimately have
 * different axes and different Retail Identifier libraries, and the table draws one header row for
 * all of them. A product missing a column renders the placeholder in that column's position rather
 * than shifting every cell after it.
 *
 * @param input - The page's per-product column sets, the Length setting, and the two structural headers.
 * @returns Structural columns, then the sticky Product Number, then the server columns.
 */
export function buildRangePlanIdentifierColumns(input: {
  columnsByProduct: ReadonlyArray<ReadonlyArray<ResolvedColumn>>;
  lengthEnabled: boolean;
  imageHeader: string;
  productNumberHeader: string;
}): RangePlanTableColumn[] {
  const { columnsByProduct, lengthEnabled } = input;

  const seen = new Set<string>();
  const collected: Array<{ column: ResolvedColumn; firstSeen: number }> = [];

  columnsByProduct.forEach((columns) => {
    (columns ?? []).forEach((column) => {
      if (column.refCode === 'productNumber') return;
      if (seen.has(column.identifierId)) return;
      if (!isVisible(column, lengthEnabled)) return;

      seen.add(column.identifierId);
      collected.push({ column, firstSeen: collected.length });
    });
  });

  /*
   * Canonical position first, then first appearance. The tie-break keeps Retail Identifier columns
   * oldest-library-first and custom columns in their server sequence, both of which arrive already
   * ordered within a single product.
   */
  const serverColumns: RangePlanTableColumn[] = collected
    .sort((left, right) => {
      const byCanonical = canonicalIndex(left.column) - canonicalIndex(right.column);
      if (byCanonical !== 0) return byCanonical;
      const bothRetail = isRetailGroup(left.column) && isRetailGroup(right.column);
      return bothRetail
        ? left.column.seqNumber - right.column.seqNumber || left.firstSeen - right.firstSeen
        : left.firstSeen - right.firstSeen;
    })
    .map(({ column }, index) => ({
      key: column.identifierId,
      header: column.name,
      definition: column,
      isStructural: false,
      /*
       * The table pins its first five columns — the three structural ones, Product Number, and the
       * first server column, which the canonical order makes Colourway. That mirrors
       * PINNED_LEFT_REF_CODES on the dev-order identifier grid, so both screens keep the same two
       * content columns in view while the rest scrolls. `isFixed` marks the LAST pinned column,
       * which is the one that carries the boundary border.
       */
      isFixed: index === 0,
    }));

  return [
    { key: SELECT_COLUMN_ID, header: '', isStructural: true, isFixed: false },
    { key: EXPAND_COLUMN_ID, header: '', isStructural: true, isFixed: false },
    { key: IMAGE_COLUMN_ID, header: input.imageHeader, isStructural: true, isFixed: false },
    {
      key: '__rp_pi_number',
      header: input.productNumberHeader,
      isStructural: true,
      /* Pinned by CSS position, but not the boundary — the column after it is. */
      isFixed: false,
    },
    ...serverColumns,
  ];
}

/**
 * One product's server columns, keyed by id so the body can draw them in the page's header order.
 *
 * A product without a given column is simply absent from the map, and the body renders the
 * placeholder — which is what keeps the columns aligned across products that differ.
 *
 * @param columns - One product's resolved columns.
 * @param lengthEnabled - Whether this user sees the Length axis.
 * @returns Identifier id to column, excluding Product Number and any hidden Length column.
 */
export function productColumnMap(
  columns: ReadonlyArray<ResolvedColumn>,
  lengthEnabled: boolean,
): Map<string, ResolvedColumn> {
  const map = new Map<string, ResolvedColumn>();
  (columns ?? []).forEach((column) => {
    if (column.refCode === 'productNumber') return;
    if (!isVisible(column, lengthEnabled)) return;
    map.set(column.identifierId, column);
  });
  return map;
}

/** Memoises the header row; it changes only when the page or the Length setting does. */
export function useRangePlanIdentifierColumns(input: {
  columnsByProduct: ReadonlyArray<ReadonlyArray<ResolvedColumn>>;
  lengthEnabled: boolean;
  imageHeader: string;
  productNumberHeader: string;
}): RangePlanTableColumn[] {
  const { columnsByProduct, lengthEnabled, imageHeader, productNumberHeader } = input;

  return useMemo(
    () =>
      buildRangePlanIdentifierColumns({
        columnsByProduct,
        lengthEnabled,
        imageHeader,
        productNumberHeader,
      }),
    [columnsByProduct, lengthEnabled, imageHeader, productNumberHeader],
  );
}

/** Ref codes that sort in the retail slot: each barcode type column, then its GTIN-14 group. */
const RETAIL_GROUP_REF_CODES: ReadonlyArray<string> = ['__retailIdentifier', '__factorIndicator', '__gtin14'];

/**
 * Whether a column belongs to the retail group. Within it the server's `seqNumber` is fixed per
 * barcode type, so it orders the group the same way whichever product carried a type first.
 */
function isRetailGroup(column: ResolvedColumn): boolean {
  return Boolean(column.refCode && RETAIL_GROUP_REF_CODES.includes(column.refCode));
}

/**
 * Where one retail value came from, for its tooltip: entered manually, or drawn from a card — the
 * product's own card for the type unless the row names another one it fell back to.
 */
export function retailCellSource(
  row: RangePlanIdentifierRow,
  columnKey: string,
  definition: ResolvedColumn,
  retailCards: Readonly<Record<string, RetailCardSummary>> | undefined,
): { isManual: boolean; card?: RetailCardSummary } {
  if (row.manualKeys?.includes(columnKey)) {
    return { isManual: true };
  }
  const libraryId = row.retailSources?.[columnKey] ?? definition.retail?.libraryId ?? '';
  const card = retailCards?.[libraryId];
  return card ? { isManual: false, card } : { isManual: false };
}

/**
 * The action a column header offers: Assign on a GTIN-8/12/13 column, Generate on a GTIN-14 column,
 * nothing on any other.
 */
export function barcodeActionFor(column: RangePlanTableColumn): PendingBarcodeAction | null {
  const { definition } = column;
  if (definition?.retail) {
    return { kind: 'retail', label: definition.retail.barcodeType, barcodeType: definition.retail.barcodeType };
  }
  if (definition?.factor?.kind === 'gtin14') {
    return { kind: 'gtin14', label: column.header, factorIndicatorId: definition.factor.factorIndicatorId };
  }
  return null;
}
